import React, { createContext, useContext } from 'react'

import { type Role, type User } from './types'
import { type Bill, type Contract } from '../features/Contract/utils/types'
import { type Request, type Offer } from '../features/Request/utils/types'
import { type NewPermissions } from '../features/Roles/utils/types'
import { type GuestRequest } from '../features/UnauthRequest/utils/types'
import { type PaginatedQueryParameters } from '../api/hooks/utils/types'
import { type GuestRequestFilters } from '../features/UnauthRequest/hooks/useGuestRequestQueries'
import { type RequestFilters, type OfferFilters } from '../features/Request/api/requestApi'
import { type ContractFilters, type BillFilters } from '../features/Contract/api/contractApi'

export enum ContractTabType {
  GUEST_REQUESTS = 'GUEST_REQUESTS',
  REQUESTS = 'REQUESTS',
  OFFERS = 'OFFERS',
  CONTRACTS = 'CONTRACTS',
  BILLS = 'BILLS',
}

type UserContextType = {
  accessToken: string
  setAccessToken: (accessToken: string) => void
  userPermissions: NewPermissions
  roles: Role[]
  user: User | undefined
  setUser: (user: User | undefined) => void
}

type CountContextType = {
  initialGuestRequests: GuestRequest[]
  setInitialGuestRequests: React.Dispatch<React.SetStateAction<GuestRequest[]>>
  initialGuestRequestsCount: number
  setInitialGuestRequestsCount: React.Dispatch<React.SetStateAction<number>>
  initialRequests: Request[]
  setInitialRequests: React.Dispatch<React.SetStateAction<Request[]>>
  initialRequestsCount: number
  setInitialRequestsCount: React.Dispatch<React.SetStateAction<number>>
  initialOffers: Offer[]
  setInitialOffers: React.Dispatch<React.SetStateAction<Offer[]>>
  initialOffersCount: number
  setInitialOffersCount: React.Dispatch<React.SetStateAction<number>>
  initialContracts: Contract[]
  setInitialContracts: React.Dispatch<React.SetStateAction<Contract[]>>
  initialContractsCount: number
  setInitialContractsCount: React.Dispatch<React.SetStateAction<number>>
  initialBills: Bill[]
  setInitialBills: React.Dispatch<React.SetStateAction<Bill[]>>
  initialBillsCount: number
  setInitialBillsCount: React.Dispatch<React.SetStateAction<number>>
  countTotals: number
  setCountTotals: React.Dispatch<React.SetStateAction<number>>
  tabValue: number
  setTabValue: (value: number) => void
  activeTabType: ContractTabType | null
  setActiveTabType: (type: ContractTabType | null) => void
  guestRequestQueryOptions: PaginatedQueryParameters<GuestRequest, GuestRequestFilters>
  setGuestRequestQueryOptions: React.Dispatch<React.SetStateAction<PaginatedQueryParameters<GuestRequest, GuestRequestFilters>>>
  requestQueryOptions: PaginatedQueryParameters<Request, RequestFilters>
  setRequestQueryOptions: React.Dispatch<React.SetStateAction<PaginatedQueryParameters<Request, RequestFilters>>>
  offerQueryOptions: PaginatedQueryParameters<Offer, OfferFilters>
  setOfferQueryOptions: React.Dispatch<React.SetStateAction<PaginatedQueryParameters<Offer, OfferFilters>>>
  contractQueryOptions: PaginatedQueryParameters<Contract, ContractFilters>
  setContractQueryOptions: React.Dispatch<React.SetStateAction<PaginatedQueryParameters<Contract, ContractFilters>>>
  billQueryOptions: PaginatedQueryParameters<Bill, BillFilters>
  setBillQueryOptions: React.Dispatch<React.SetStateAction<PaginatedQueryParameters<Bill, BillFilters>>>
}

export const UserContext = createContext<UserContextType | undefined>(undefined)

export const CountContext = createContext<CountContextType | undefined>(undefined)

export function useUserContext(): UserContextType {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider')
  }
  return context
}

export function useCountContext(): CountContextType {
  const context = useContext(CountContext)
  if (!context) {
    throw new Error('useCountContext must be used within a CountProvider')
  }
  return context
}
